import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'
import DisconnectedData from './DisconnectedData.container'
import LoadingContent from '../../components/Placeholders/LoadingContent'
import { Paper, Box, Grid } from '@material-ui/core'
import Typography from '../../components/Basics/Typography'
import HealthTimeline from './HealthTimeline'
import FleetHealthChart from './FleetHealthChart'
import HealthTransitionsTable from './HealthTransitionsTable'

// Freshness thresholds per source, in hours: green < amber, amber <= red, red > red.
// A frozen timestamp means the feed is down, so a linearly growing age is the signal.
// Ajustados 2026-10-20: el canario de flota (backend) ya cubre los cortes del feed
// completo, asi que estos umbrales por camion pueden ser agresivos — rojo aqui
// significa "este camion tiene un problema propio", no "el feed esta lento".
const FRESHNESS_THRESHOLDS = {
  gps: { amber: 2, red: 4 },
  loads: { amber: 4, red: 8 },
  carga: { amber: 24, red: 48 }
}

// same palette as the truck badges (config/styles/theme.js brand colors)
const SEVERITY_STYLES = [
  { dot: '#2FA12B', text: '#237920' }, // 0: green
  { dot: '#F7C137', text: '#AB7D07' }, // 1: amber
  { dot: '#AD000F', text: '#AD000F' }, // 2: red
  { dot: '#828282', text: '#828282' } // 3: sin data
]

// "YYYY-MM-DD HH:MM:SS" in mine-local time; `date` may carry a -04:00/-03:00 offset.
// Offset-aware when present, local-naive otherwise — never UTC.
const parseMineLocal = value => {
  if (!value || typeof value !== 'string') return null
  const hasOffset = /[+-]\d{2}:?\d{2}$/.test(value.trim())
  const parsed = hasOffset
    ? moment.parseZone(value.trim(), 'YYYY-MM-DD HH:mm:ssZ', true)
    : moment(value.trim(), 'YYYY-MM-DD HH:mm:ss', true)
  return parsed.isValid() ? parsed : null
}

const humanizeAge = ageHours => {
  if (ageHours < 1) return '<1 h'
  if (ageHours < 48) return `${Math.round(ageHours)} h`
  return `${Math.round(ageHours / 24)} d`
}

const getSeverity = (ageHours, thresholds) => {
  if (ageHours === null) return 3
  if (ageHours > thresholds.red) return 2
  if (ageHours >= thresholds.amber) return 1
  return 0
}

/**
 * The FleetNow's container.
 * One tile per truck showing the age of the last data seen from each source
 * (GPS, dispatch loads, fuel events). Current state only, no history.
 */
const FleetNow = () => {
  const { error, loading, trucks, saludTimeline } = useSelector(state => state.fuelLevel)
  const [now, setNow] = useState(moment())

  // ages must keep ticking even without new websocket messages
  useEffect(() => {
    const interval = setInterval(() => setNow(moment()), 60000)
    return () => clearInterval(interval)
  }, [])

  const tiles = trucks
    .map(truck => {
      const sources = [
        { key: 'gps', label: 'GPS', raw: truck.date_gps },
        { key: 'loads', label: 'Loads', raw: truck.date_loads },
        { key: 'carga', label: 'Últ. carga', raw: truck.date_fuel }
      ].map(source => {
        const timestamp = parseMineLocal(source.raw)
        const ageHours = timestamp ? Math.max(0, now.diff(timestamp, 'hours', true)) : null
        return {
          ...source,
          ageHours,
          ageLabel: ageHours === null ? 's/d' : humanizeAge(ageHours),
          severity: getSeverity(ageHours, FRESHNESS_THRESHOLDS[source.key])
        }
      })

      const worstSeverity = Math.max(...sources.map(source => source.severity))
      const worstAge = Math.max(...sources.map(source => (source.ageHours === null ? Infinity : source.ageHours)))
      return { patent: truck.patent, sources, worstSeverity, worstAge }
    })
    .sort((a, b) => b.worstSeverity - a.worstSeverity || b.worstAge - a.worstAge)

  const legend = Object.entries({
    GPS: FRESHNESS_THRESHOLDS.gps,
    Loads: FRESHNESS_THRESHOLDS.loads,
    'Últ. carga': FRESHNESS_THRESHOLDS.carga
  })
    .map(([label, t]) => `${label}: verde < ${t.amber} h · ámbar ${t.amber}–${t.red} h · rojo > ${t.red} h`)
    .join('  —  ')

  // tile accent = worst of its three colors; "s/d" accents gray
  const accentColor = severity => SEVERITY_STYLES[severity].dot

  return (
    <>
      {loading && <LoadingContent />}
      {!loading && error && <DisconnectedData />}
      {!loading && !error && (
        <Box style={{ marginLeft: '10px', width: '100%' }}>
          <Typography variant="h2" margin="0 0 5px 0">
            Estatus Datos: antigüedad del último dato recibido por fuente. Un valor que crece de forma sostenida indica
            que la fuente está caída.
          </Typography>
          <Typography variant="body2" margin="0 0 20px 0">
            {legend}
          </Typography>
          {tiles.length === 0 ? (
            <Typography variant="h2" margin="0 0 20px 0">
              Sin data
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {tiles.map(tile => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={tile.patent}>
                  <Paper
                    style={{
                      padding: '10px 12px',
                      borderLeft: `5px solid ${accentColor(tile.worstSeverity)}`
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold" margin="0 0 6px 0">
                      {tile.patent}
                    </Typography>
                    {tile.sources.map(source => (
                      <div
                        key={source.key}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', fontSize: '12px', color: '#4F4F4F' }}>
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              marginRight: '6px',
                              display: 'inline-block',
                              backgroundColor: SEVERITY_STYLES[source.severity].dot
                            }}
                          />
                          {source.label}
                        </span>
                        <span
                          style={{
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: SEVERITY_STYLES[source.severity].text
                          }}
                        >
                          {source.ageLabel}
                        </span>
                      </div>
                    ))}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
          {/* salud_timeline document may be absent (deploy gaps) → hide these views, no error */}
          {saludTimeline && (
            <>
              <HealthTimeline salud={saludTimeline} />
              <FleetHealthChart salud={saludTimeline} />
              <HealthTransitionsTable salud={saludTimeline} />
            </>
          )}
        </Box>
      )}
    </>
  )
}

export default FleetNow
